import { useEffect, useRef, useState } from 'react'
import { Trans } from 'react-i18next'

import {
  COND_FEATURE_FLAG_ALL,
  ConditionBooleanOperator,
  ConditionExpression,
  ConditionFeatureFlag,
  ConditionNumericOperator,
  ConditionStatementCustomText,
  ConditionTextOperator,
  ConditionValue,
  DATE_OPERATOR_WITH_RANGE_RHS,
  DATE_OPERATOR_WITH_RHS,
  DefaultConditionOperator,
  NUMERIC_OPERATOR_WITH_RANGE_RHS,
  OPERATOR_WITH_INPUT_RHS,
} from '@makyoapp/makyo-common'
import { ChevronDown, Plus16 } from '@makyoapp/makyo-icon/icon'
import { MaterialIcon } from '@makyoapp/makyo-tw-ui'
import { Button, ButtonVariant } from '@makyoapp/makyo-ui'

import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { ComponentSettingKey } from '@/shared/builder/types'
import { getCurrentFormFieldColumns } from '@/shared/form-utils'
import { filterComponentInBody } from '@/utils/builder/app-builder'

import { ConditionOperatorSelectV2 } from './ConditionOperatorSelectV2'
import { ConditionRepeaterContext, useConditionRepeaterContext } from './ConditionRepeaterContext'
import { ConditionSelectV2 } from './ConditionSelectV2'

function ConditionItem({ index }: { index: number }) {
  const { id, onChange, value } = useConditionRepeaterContext()
  const cond = value.conditions[index]
  const totalConditions = value.conditions.length
  const [showLogicDropdown, setShowLogicDropdown] = useState(false)
  const logicDropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (logicDropdownRef.current && !logicDropdownRef.current.contains(event.target as Node)) {
        setShowLogicDropdown(false)
      }
    }

    if (showLogicDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showLogicDropdown])

  const handleOperatorChange = (opt: DefaultConditionOperator) => {
    const nextCond = [...value.conditions]
    const isSame = cond.operator === opt
    const needRhs = OPERATOR_WITH_INPUT_RHS.includes(opt)
    const isRange = NUMERIC_OPERATOR_WITH_RANGE_RHS.includes(opt as ConditionNumericOperator)
    const isDate = DATE_OPERATOR_WITH_RHS.includes(opt)
    const isDateRange = DATE_OPERATOR_WITH_RANGE_RHS.includes(opt)

    const nextRhs = isSame
      ? nextCond[index].rhs
      : isDate
        ? ({
            flag: ConditionFeatureFlag.CustomText,
            value: new Date().toISOString(),
          } as ConditionStatementCustomText)
        : isDateRange
          ? ({
              flag: ConditionFeatureFlag.CustomText,
              value: new Date().toISOString(),
              endValue: new Date().toISOString(),
            } as ConditionStatementCustomText)
          : ({
              flag: ConditionFeatureFlag.CustomText,
              // @ts-ignore
              value: cond.rhs?.value,
              // @ts-ignore
              endValue: isRange ? cond.rhs?.endValue : undefined,
            } as ConditionStatementCustomText)

    nextCond[index] = {
      ...nextCond[index],
      operator: opt,
      rhs: nextRhs,
    }

    if (onChange) {
      onChange({
        isAny: value.isAny,
        conditions: nextCond,
      })
    }
  }

  const handleRemoveCondition = () => {
    const nextCond = [...value.conditions]
    nextCond.splice(index, 1)

    if (onChange) {
      onChange({
        isAny: value.isAny,
        conditions: nextCond,
      })
    }
  }

  return (
    <>
      {/* AND/OR Button - Show between conditions */}
      {index > 0 && (
        <div className='flex justify-center w-full relative' ref={logicDropdownRef}>
          <button
            onClick={() => setShowLogicDropdown(!showLogicDropdown)}
            className='flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-semibold text-sm rounded-md transition-colors'
            aria-label={`Change logic operator`}
            aria-expanded={showLogicDropdown}
          >
            <span>{value.isAny ? 'OR' : 'AND'}</span>
            <ChevronDown className={`h-4 w-4 transition-transform ${showLogicDropdown ? 'rotate-180' : ''}`} />
          </button>

          {showLogicDropdown && (
            <div className='absolute top-full left-1/2 transform -translate-x-1/2 mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded shadow-lg z-10 min-w-[120px]'>
              <button
                onClick={() => {
                  onChange?.({
                    ...value,
                    isAny: false,
                  })
                  setShowLogicDropdown(false)
                }}
                className={`w-full text-left px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                  !value?.isAny ? 'text-teal-500' : 'text-gray-700 dark:text-gray-300'
                }`}
              >
                <Trans>AND</Trans>
              </button>
              <button
                onClick={() => {
                  onChange?.({
                    ...value,
                    isAny: true,
                  })
                  setShowLogicDropdown(false)
                }}
                className={`w-full text-left px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                  value?.isAny ? 'text-teal-500' : 'text-gray-700 dark:text-gray-300'
                }`}
              >
                <Trans>OR</Trans>
              </button>
            </div>
          )}
        </div>
      )}

      <div className='flex items-center gap-2 w-full flex-col bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700'>
        {/* <div className='flex gap-2 items-center justify-between w-full'>
          <div className='flex gap-3 items-center'>
            <div className='text-sm font-medium text-gray-700 dark:text-gray-400'>
              <Trans>Condition</Trans> {index + 1}
            </div>
          </div>
        </div> */}
        <div className='flex flex-col gap-2 w-full'>
          <div className='flex-c w-full'>
            <ConditionSelectV2 id={id ? `${id}-${index}-lhs` : undefined} index={index} />
            <button
              onClick={handleRemoveCondition}
              className='cursor-pointer flex items-center justify-center self-center hover:bg-gray-100 dark:hover:bg-gray-700 rounded p-2 transition-colors border border-gray-700 w-10 h-10 shrink-0'
              aria-label='Remove condition'
            >
              <MaterialIcon icon='delete' size={18} />
            </button>
          </div>

          <ConditionOperatorSelectV2
            id={id ? `${id}-${index}-op` : undefined}
            index={index}
            selected={cond.operator}
            value={value}
            onChange={handleOperatorChange}
          />

          {/* single value (all) */}
          {OPERATOR_WITH_INPUT_RHS.includes(cond.operator) && (
            <ConditionSelectV2 id={id ? `${id}-${index}-rhs` : undefined} index={index} isRhs={true} />
          )}

          {/* single value (date) */}
          {DATE_OPERATOR_WITH_RHS.includes(cond.operator) && (
            <ConditionSelectV2
              id={id ? `${id}-${index}-rhs-date` : undefined}
              isRhs={true}
              index={index}
              isDate={true}
            />
          )}

          {/* range */}
          {[...DATE_OPERATOR_WITH_RANGE_RHS, ...NUMERIC_OPERATOR_WITH_RANGE_RHS].includes(cond.operator) && (
            <div className='mt-1 flex flex-col gap-4'>
              <div className='space-y-1'>
                <div className='uppercase text-m-ss'>
                  <Trans>Lower Bound</Trans>
                </div>
                <ConditionSelectV2
                  key={`${id}-${index}-rhs-start`}
                  id={id ? `${id}-${index}-rhs-start` : undefined}
                  isRhs={true}
                  index={index}
                  isDate={true}
                />
              </div>
              <div className='space-y-1'>
                <div className='uppercase text-m-ss'>
                  <Trans>Upper Bound</Trans>
                </div>
                <ConditionSelectV2
                  key={`${id}-${index}-rhs-end`}
                  id={id ? `${id}-${index}-rhs-end` : undefined}
                  isRhs={true}
                  index={index}
                  isEnd
                  isDate={true}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

interface ConditionRepeaterProps {
  id?: string
  value: ConditionValue
  onChange?: (nextValue: ConditionValue) => void
  featureFlags?: ConditionFeatureFlag
  parentFormId?: string
}

export function ConditionRepeaterV3({
  id,
  value = {
    isAny: false,
    conditions: [],
  },
  onChange,
  featureFlags = COND_FEATURE_FLAG_ALL,
  parentFormId,
}: ConditionRepeaterProps) {
  const { tables } = useDbClientContext()
  const { activeLayerClient } = useActiveLayerContext()
  const [showLogicDropdown, setShowLogicDropdown] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowLogicDropdown(false)
      }
    }

    if (showLogicDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showLogicDropdown])

  const getDefaultCondition = (): ConditionExpression[] => {
    // form elements: generate default form condition
    if (parentFormId) {
      const containerSettings = activeLayerClient?.settings._BodySettings
      const form = activeLayerClient?.componentsMap.get(parentFormId as string)

      if (form) {
        const formChildren = filterComponentInBody(form._Body, (el) => ['form', 'upload'].includes(el._GroupID))
        const formSettings = form?.settings
        const dataSource = formSettings?.[ComponentSettingKey.FormDataSource]
        const table = tables.find((t) => t.id === dataSource)

        if (containerSettings && formChildren && table) {
          const columns = getCurrentFormFieldColumns(containerSettings, formChildren)
          const firstColId = columns[0]

          return [
            {
              lhs: { flag: ConditionFeatureFlag.FormData, formId: parentFormId, col: firstColId },
              operator: ConditionTextOperator.IsNotEmpty,
            },
          ]
        }
      }
    }

    // other elements: generate default user logged in condition
    return [
      {
        lhs: { flag: ConditionFeatureFlag.UserLoggedIn },
        operator: ConditionBooleanOperator.IsFalse,
      },
    ]
  }

  const handleAddCondition = () => {
    if (onChange) {
      onChange({
        isAny: value.isAny,
        conditions: [...(value.conditions || []), ...getDefaultCondition()],
      })
    }
  }

  return (
    <ConditionRepeaterContext.Provider value={{ id, value, onChange, featureFlags, parentFormId }}>
      <div id={id} className='flex flex-col gap-4 w-full'>
        {/* Logic Mode Selector */}
        <div className='mb-2 relative' ref={dropdownRef}>
          {/* <button
            onClick={() => setShowLogicDropdown(!showLogicDropdown)}
            className='flex items-center justify-between w-full bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-4 py-3 rounded border border-gray-300 dark:border-gray-800 hover:border-gray-400 dark:hover:border-gray-500 transition-colors'
            aria-label='Select logic mode'
            aria-expanded={showLogicDropdown}
          >
            <span>
              {value?.isAny ? (
                <Trans>At least one condition must be met (OR)</Trans>
              ) : (
                <Trans>All conditions must be met (AND)</Trans>
              )}
            </span>
            <ChevronDown className={`h-4 w-4 transition-transform ${showLogicDropdown ? 'rotate-180' : ''}`} />
          </button> */}

          {showLogicDropdown && (
            <div className='absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded shadow-lg z-10'>
              <button
                onClick={() => {
                  onChange?.({
                    ...value,
                    isAny: false,
                  })
                  setShowLogicDropdown(false)
                }}
                className={`w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                  !value?.isAny ? 'text-teal-500' : 'text-gray-700 dark:text-gray-300'
                }`}
              >
                <Trans>All conditions must be met</Trans>
              </button>
              <button
                onClick={() => {
                  onChange?.({
                    ...value,
                    isAny: true,
                  })
                  setShowLogicDropdown(false)
                }}
                className={`w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                  value?.isAny ? 'text-teal-500' : 'text-gray-700 dark:text-gray-300'
                }`}
              >
                <Trans>At least one condition must be met</Trans>
              </button>
            </div>
          )}
        </div>

        {/* Description Text - Show when there are conditions */}
        {value && value.conditions?.length > 0 && (
          <p className='text-sm text-gray-600 dark:text-gray-400'>
            {value.isAny ? (
              <Trans>
                Show content when <strong>any</strong> of the conditions below are met. Add more conditions to refine
                visibility rules.
              </Trans>
            ) : (
              <Trans>
                Show content when <strong>all</strong> of the conditions below are met. Add more conditions to refine
                visibility rules.
              </Trans>
            )}
          </p>
        )}

        {value && value.conditions?.length > 0 && (
          <div className='flex gap-3 flex-col w-full min-w-0'>
            {value.conditions.map((_, i) => {
              return <ConditionItem index={i} key={i} />
            })}
          </div>
        )}

        <div className='flex justify-start'>
          <Button
            onClick={handleAddCondition}
            variant={ButtonVariant.Ghost}
            className='cursor-pointer flex items-center !text-primary-500 hover:text-primary-600'
          >
            <MaterialIcon size={18} icon='add' />
            <Trans>Add Condition</Trans>
          </Button>
        </div>
      </div>
    </ConditionRepeaterContext.Provider>
  )
}
